import { useState, useEffect } from 'react';
import { isUserLoggedIn } from '../utils/cookieUtils';

/**
 * Custom hook to check authentication status using the login status cookie
 * This provides a fast way to determine if user is logged in without API calls
 * @returns {Object} { isAuthenticated, isLoading }
 */
export function useAuthStatus() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check cookie on component mount
    const checkAuthStatus = () => {
      const loggedIn = isUserLoggedIn();
      setIsAuthenticated(loggedIn);
      setIsLoading(false);
    };

    checkAuthStatus();

    // Optional: Listen for cookie changes by polling
    // This is useful if login status changes in another tab
    const interval = setInterval(checkAuthStatus, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, []);

  return { isAuthenticated, isLoading };
}

/**
 * Custom hook that returns a function to manually refresh auth status
 * Useful after login/logout operations
 * @returns {Function} refreshAuthStatus function
 */
export function useAuthStatusRefresh() {
  const refreshAuthStatus = () => {
    // Trigger a custom event that can be listened to by useAuthStatus
    window.dispatchEvent(new CustomEvent('authStatusChanged'));
  };

  return refreshAuthStatus;
}

/**
 * Enhanced version of useAuthStatus that listens for auth status changes
 * @returns {Object} { isAuthenticated, isLoading, refreshAuthStatus }
 */
export function useAuthStatusWithRefresh() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = () => {
    const loggedIn = isUserLoggedIn();
    setIsAuthenticated(loggedIn);
    setIsLoading(false);
  };

  useEffect(() => {
    checkAuthStatus();

    // Listen for custom auth status change events
    const handleAuthStatusChange = () => {
      checkAuthStatus();
    };

    window.addEventListener('authStatusChanged', handleAuthStatusChange);

    // Optional: Poll for changes
    const interval = setInterval(checkAuthStatus, 5000);
    
    return () => {
      window.removeEventListener('authStatusChanged', handleAuthStatusChange);
      clearInterval(interval);
    };
  }, []);

  const refreshAuthStatus = () => {
    checkAuthStatus();
    window.dispatchEvent(new CustomEvent('authStatusChanged'));
  };

  return { isAuthenticated, isLoading, refreshAuthStatus };
}

export default useAuthStatus;
